import React, { useMemo, useState } from "react";
import AppShell from "../components/ui/AppShell";
import BrandLogo from "../components/BrandLogo";
import { FaCopy, FaCheck, FaChrome } from "react-icons/fa";
import "./ExtensionLicensePage.css";

const EXTENSION_STORE_URL =
  "https://chromewebstore.google.com/detail/vfs-visa-portal-bridge/hhbhkichdehlhfcegpemkghjkjnabeng?authuser=0&hl=en";

const STEPS = [
  <>Install / open the <strong>VFS Bridge</strong> Chrome extension.</>,
  <>Copy the license key from this page.</>,
  <>Paste it in the extension and click <strong>Verify</strong>.</>,
  <>Wait until Connection shows <strong>Connected</strong>.</>,
  <>Create a booking job from VFS.</>,
];

function formatLicenseKey(userId) {
  const id = Number(userId);
  if (!Number.isInteger(id) || id <= 0) return null;
  return `VP-${String(id).padStart(6, "0")}`;
}

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

const ExtensionLicensePage = () => {
  const user = useMemo(() => readStoredUser(), []);
  const licenseKey = useMemo(() => formatLicenseKey(user?.id), [user]);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!licenseKey) return;

    try {
      await navigator.clipboard.writeText(licenseKey);
    } catch (_err) {
      const input = document.createElement("textarea");
      input.value = licenseKey;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <AppShell>
      <div className="ext-license">
        <article className="ext-license-card">
          <header className="ext-license-hero">
            <div className="ext-license-brand">
              <BrandLogo asLink={false} height={56} className="ext-license-logo" />
              <div className="ext-license-heading">
                <div className="ext-license-kicker">
                  <span className="ext-license-chip">VFS Bridge</span>
                  <span>License connection</span>
                </div>
                <div className="ext-license-title-row">
                  <div>
                    <h1 className="ext-license-title">Extension License</h1>
                    <p className="ext-license-desc">
                      Use this key to connect the VFS Bridge Chrome extension to your account.
                    </p>
                  </div>
                  <a
                    className="btn btn-primary ext-license-store"
                    href={EXTENSION_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaChrome className="me-2" />
                    Get Extension
                  </a>
                </div>
              </div>
            </div>
          </header>

          <div className="ext-license-grid">
            <section className="ext-license-panel">
              <h2 className="ext-license-panel-title">How to connect</h2>
              <ol className="ext-license-steps">
                {STEPS.map((step, index) => (
                  <li key={index}>
                    <span className="ext-license-step-index">{index + 1}</span>
                    <span className="ext-license-step-copy">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section className="ext-license-panel ext-license-key-panel">
              <h2 className="ext-license-panel-title">Your license key</h2>

              {licenseKey ? (
                <>
                  <p className="ext-license-key-meta">
                    Linked to {user?.username || "your account"}
                    {user?.id ? ` (#${user.id})` : ""}
                  </p>
                  <div className="ext-license-key-row">
                    <code>{licenseKey}</code>
                    <button
                      type="button"
                      className={`btn ${copied ? "btn-success" : "btn-primary"}`}
                      onClick={handleCopy}
                    >
                      {copied ? (
                        <>
                          <FaCheck className="me-2" />
                          Copied
                        </>
                      ) : (
                        <>
                          <FaCopy className="me-2" />
                          Copy key
                        </>
                      )}
                    </button>
                  </div>
                  <p className="ext-license-note">
                    Keep this key private. Anyone with it can connect an extension as your account.
                  </p>
                </>
              ) : (
                <div className="alert alert-warning mb-0">
                  Could not load your license key. Please log out and log in again.
                </div>
              )}
            </section>
          </div>
        </article>
      </div>
    </AppShell>
  );
};

export default ExtensionLicensePage;
